import { useState, useEffect, useRef } from 'react';
import { fetchParkingForBounds, type DkParkingSpot } from '../services/parkingDkService';
import type { MapBounds } from '../types';
import { boundsOverlapRatio } from '../utils/boundsUtils';

/**
 * Danske parkeringsplasser fra GeoDanmark, hentet som statiske geocellefiler.
 *
 * Speiler `useRoadBarriers`: debounce, overlapp-sjekk mot forrige henting, og
 * tømming når laget skrus av. Forskjellen er at det ikke går et eneste kall til
 * Firestore eller en Cloud Function — filene ligger bak Hosting-CDN-en.
 *
 * `minZoom` er 12 og ikke 13 som barrierene: en parkeringsplass er relevant litt
 * lenger ute enn en bom, og cellene er allerede hentet og cachet.
 */
export const DK_PARKING_MIN_ZOOM = 12;

export function useDenmarkParking(
  active: boolean,
  zoom: number,
  currentViewBounds: MapBounds | null,
): { parkering: DkParkingSpot[]; isLoading: boolean } {
  const [parkering, setParkering] = useState<DkParkingSpot[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const sistHentet = useRef<MapBounds | null>(null);

  useEffect(() => {
    if (!active || zoom < DK_PARKING_MIN_ZOOM || !currentViewBounds) {
      if (!active) { setParkering([]); sistHentet.current = null; }
      return;
    }
    // Samme terskel som barrierene: under 30 % forskyvning er det ikke verdt en
    // ny runde. Cellene er cachet, men klippingen koster.
    if (sistHentet.current && boundsOverlapRatio(sistHentet.current, currentViewBounds) > 0.7) return;

    let avbrutt = false;
    const timer = setTimeout(async () => {
      setIsLoading(true);
      try {
        const p = await fetchParkingForBounds(currentViewBounds);
        if (avbrutt) return;
        setParkering(p);
        sistHentet.current = currentViewBounds;
      } catch (e) {
        console.error('Klarte ikke laste danske parkeringsplasser:', e);
      } finally {
        if (!avbrutt) setIsLoading(false);
      }
    }, 400);

    return () => { avbrutt = true; clearTimeout(timer); };
  }, [active, zoom, currentViewBounds]);

  return { parkering, isLoading };
}
